import type { FetchFunction, GraphQLResponse } from 'relay-runtime';
import { Observable } from 'relay-runtime';

import { getLogger } from '@confluence/logger';
import { SessionExpiredError } from '@confluence/network';
import { ClientNetworkStatus } from '@confluence/legacy-schema-bridge/entry-points/useNetworkStatusListenerCache';

import {
	updateNetworkStatusUnlessSessionExpired,
	hasSessionExpiredErrors,
} from './networkStatusMiddlewareUtils';
import { ERROR_HANDLERS } from './networkStatusMiddlewareConfig';

interface SessionExpiredErrorWithContext extends SessionExpiredError {
	originalError?: unknown;
}

export const createNetworkStatusMiddleware = (fetchFn: FetchFunction): FetchFunction => {
	const logger = getLogger(createNetworkStatusMiddleware);

	return (request, variables, cacheConfig) => {
		return Observable.create((observer) => {
			const subscription = Observable.from(fetchFn(request, variables, cacheConfig)).subscribe({
				next(result: GraphQLResponse) {
					if (!hasSessionExpiredErrors(result)) {
						updateNetworkStatusUnlessSessionExpired(ClientNetworkStatus.ONLINE);
						observer.next(result);
						return;
					}
					updateNetworkStatusUnlessSessionExpired(ClientNetworkStatus.SESSION_EXPIRED);
					const errors = 'errors' in result ? result.errors : undefined;

					// We detect session expiry from GraphQL errors, not HTTP status, so we create a synthetic 401 Response.
					// TODO: Refactor SessionExpiredError to not require Response object.
					// Note: traceId may already be captured in BatchFetcher.captureErrors, but is unavailable in this error wrapper.
					const sessionExpiredError: SessionExpiredErrorWithContext = new SessionExpiredError(
						request.name,
						new Response(null, { status: 401, statusText: 'Unauthorized' }),
					);
					// Preserve original errors in both `cause` (Error convention) and `originalError` (GraphQL convention)
					sessionExpiredError.originalError = sessionExpiredError.cause = errors;
					observer.error(sessionExpiredError);
				},

				error(error: Error) {
					// Check error handlers in priority order
					for (const handler of ERROR_HANDLERS) {
						if (handler.check(error)) {
							void logger.log`${handler.logMessage} for ${request.name}`;
							updateNetworkStatusUnlessSessionExpired(handler.status);

							if (handler.conditionalPropagate) {
								handler.conditionalPropagate(error, observer);
							} else if (handler.propagateError) {
								observer.error(error);
							}
							return;
						}
					}

					observer.error(error);
				},

				complete() {
					observer.complete();
				},
			});

			return () => subscription.unsubscribe();
		});
	};
};
